import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U2103";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  GETSECRETKEY: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  UPDATEPWD: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  LOADLOGICALUNIT: () => string;
  LOADROLE: string;
  EXPORT_EXCEL: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  GETSECRETKEY: (id: string | number) => `${LOGICALUNIT_URL}/${id}/secretkey`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  UPDATEPWD: (id: string | number) => `${LOGICALUNIT_URL}/updatepwd/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  LOADLOGICALUNIT: () => `${LOGICALUNIT_URL}/loadLogicalUnitActions`,
  LOADROLE: `${LOGICALUNIT_URL}/loadroles`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
};

export interface U2103User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  active: boolean;
  locked: boolean;
  password: string;
  changePasswordOnLogin: boolean;
  secretKey: string;
  lastLogin: string;
  nbOfFailedAttempts: number;
  /** BL-0520 — connexion Google exclusive. */
  ssoEnabled: boolean;
  /** BL-0520 — instant ISO de liaison Google (lecture seule). */
  googleLinkedAt?: string | null;
  /** BL-0520 — effacer google_sub à la désactivation SSO. */
  unlinkGoogleIdentity?: boolean;
  roles: U2103UserRole[];
  actions: U2103UserLogicalUnit[];
  affiliations?: U2103UserAffiliation[];
}

export interface U2103UserRole {
  id: string;
  idChecked: boolean;
  roleCode: string;
  roleDescription: string;
}

export interface U2103UserLogicalUnit {
  id: string;
  logicalUnitCode: string;
  logicalUnitDescription: string;
  actions: string[];
}

export interface U2103UserAffiliation {
  id: string;
  organizationName: string;
  createdAt?: string;
}

export interface U2103Role {
  id: string;
  idChecked: boolean;
  roleCode: string;
  roleDescription: string;
}

export interface U2103LogicalUnit extends Record<string, unknown> {
  id: string;
  logicalUnitCode: string;
  logicalUnitDescription: string;
  logicalUnitActions: U2103LogicalUnitAction[];
}

export interface U2103LogicalUnitAction {
  id: string;
  idChecked: boolean;
  logicalUnitActionCode: string;
  logicalUnitActionDescription: string;
}

export const emptyUser: U2103User = {
  id: "",
  username: "",
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  active: true,
  locked: false,
  changePasswordOnLogin: true,
  secretKey: "",
  lastLogin: "",
  nbOfFailedAttempts: 0,
  ssoEnabled: false,
  googleLinkedAt: null,
  unlinkGoogleIdentity: false,
  roles: [],
  actions: [],
};

export const emptyLogicalUnit: U2103LogicalUnit = {
  id: "",
  logicalUnitCode: "",
  logicalUnitDescription: "",
  logicalUnitActions: [],
};

export const emptyUserRole: U2103UserRole = {
  id: "",
  idChecked: false,
  roleCode: "",
  roleDescription: "",
};

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doGetSecretKeyData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = forceRefresh ? { b: U2000GenerateUrlUUID() } : {};
  return axios.get(LogicalUnitEndpoints.GETSECRETKEY(id), { params });
};

/** Payload create/update : password omis si SSO (contrat BL-0520 / directives U2103). */
export type U2103UserSavePayload = Omit<U2103User, 'password'> & { password?: string };

export const doCreateData = (axios: AxiosInstance, data: U2103UserSavePayload) => {
  return axios.post(LogicalUnitEndpoints.CREATE, data);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, data: U2103UserSavePayload) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), data);
};

export const doUpdatePwdData = (axios: AxiosInstance, id: string | number, data: U2103User) => {
  return axios.post(LogicalUnitEndpoints.UPDATEPWD(id), data);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, data?: U2103User) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), data);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export const doLoadLogicalUnitActions = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.LOADLOGICALUNIT());
};

export const doLoadRoles = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.LOADROLE);
};

// API Calls - Export Excel
export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang });
};

export function luHandleAppiError(t: TranslateFn, NS: string, err: any, errorResult: U2000ApiErrorResult) {
  if (err.errorCode === "BU210301") {
    errorResult.fieldErrors["password"] = t("errorBU210301", { ns: NS });
  } else if (err.errorCode === "BU210302") {
    errorResult.fieldErrors["password"] = t("errorBU210302", { ns: NS });
  } else if (err.errorCode === "BU210304") {
    errorResult.fieldErrors["password"] = t("errorBU210304", { ns: NS });
  }
}

/** BL-0520 — prépare le payload create/update (password omis si SSO ; unlink à la désactivation). */
export function u2103PrepareUserSavePayload(
  user: U2103User,
  previousSsoEnabled: boolean | undefined,
): U2103UserSavePayload {
  const payload: U2103UserSavePayload = { ...user };
  const disablingSso = previousSsoEnabled === true && payload.ssoEnabled === false;

  if (payload.ssoEnabled === true) {
    delete payload.password;
    payload.unlinkGoogleIdentity = false;
  } else if (disablingSso) {
    payload.unlinkGoogleIdentity = true;
  }

  return payload;
}
